import type { RecoveryRecord, RecoveryStore } from "../storage/recovery";

export interface RecoveryLocation {
  database: string;
  store: string;
  key: string;
}

/** All connections close after completion or failure; success means transaction commit. */
export class IndexedDbRecoveryStore implements RecoveryStore {
  constructor(
    private readonly factory: IDBFactory | undefined,
    private readonly location: RecoveryLocation,
  ) {}

  private open(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      if (!this.factory) return reject(new Error("IndexedDB недоступен."));
      const request = this.factory.open(this.location.database, 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(this.location.store))
          request.result.createObjectStore(this.location.store);
      };
      request.onerror = () =>
        reject(
          request.error || new Error("Не удалось открыть восстановление."),
        );
      request.onsuccess = () => {
        const database = request.result;
        database.onversionchange = () => database.close();
        resolve(database);
      };
    });
  }

  private async transact(
    mode: IDBTransactionMode,
    record?: RecoveryRecord,
  ): Promise<unknown> {
    if (!this.factory) return mode === "readonly" ? null : false;
    const database = await this.open();
    return new Promise((resolve, reject) => {
      let transaction: IDBTransaction;
      try {
        transaction = database.transaction(this.location.store, mode);
        const store = transaction.objectStore(this.location.store);
        const request =
          mode === "readonly"
            ? store.get(this.location.key)
            : store.put(record, this.location.key);
        let result: unknown = mode === "readonly" ? null : true;
        request.onsuccess = () => {
          if (mode === "readonly") result = request.result ?? null;
        };
        transaction.oncomplete = () => {
          database.close();
          resolve(result);
        };
        const fail = () => {
          database.close();
          reject(
            transaction.error ||
              request.error ||
              new Error("Не удалось сохранить восстановление."),
          );
        };
        transaction.onerror = fail;
        transaction.onabort = fail;
      } catch (error) {
        try {
          transaction!.abort();
        } catch {
          /* Already finished or not created. */
        }
        database.close();
        reject(error);
      }
    });
  }

  read() {
    return this.transact("readonly");
  }
  async write(record: RecoveryRecord) {
    return (await this.transact("readwrite", record)) === true;
  }
}
